import { GenericRecord } from '@/features/providers/playtech/types';

export const safeDecode = (input: string): string => {
  try {
    return decodeURIComponent(input);
  } catch {
    return input;
  }
};

export const decodeTwice = (s: string): string => safeDecode(safeDecode(s));

export const pickStringish = (v: unknown): string | null => {
  if (v === null || v === undefined) return null;
  if (typeof v === 'string') return v.trim();
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (Array.isArray(v)) {
    const prim = (v as unknown[]).find(
      (it) =>
        typeof it === 'string' ||
        typeof it === 'number' ||
        typeof it === 'boolean',
    );
    return prim !== undefined ? String(prim) : null;
  }
  if (typeof v === 'object') {
    const obj = v as GenericRecord;
    const k = [
      'component',
      'value',
      'label',
      'name',
      'type',
      'id',
      'key',
      'slug',
      'code',
    ].find(
      (key) =>
        typeof obj[key] === 'string' ||
        typeof obj[key] === 'number' ||
        typeof obj[key] === 'boolean',
    );
    return k ? String(obj[k as keyof typeof obj]) : null;
  }
  return null;
};

export const normalizeComponent = (value: unknown): string | null => {
  let s = pickStringish(value);
  if (!s) return null;
  const looksJson =
    (s.startsWith('{') && s.endsWith('}')) ||
    (s.startsWith('[') && s.endsWith(']'));
  if (looksJson) {
    try {
      s = pickStringish(JSON.parse(s)) ?? s;
    } catch {
      return s;
    }
  }
  return s;
};

export const normalizeHostedmode = (value: unknown): string | null => {
  const s = pickStringish(value);
  if (!s) return null;
  const n = Number(s);
  if (!Number.isFinite(n)) return null;
  return String(Math.trunc(n));
};

export const normalizeTags = (value: unknown): string | null => {
  if (value === null || value === undefined) return null;

  if (Array.isArray(value)) {
    const parts = (value as unknown[])
      .map((it) => pickStringish(it))
      .filter((it): it is string => Boolean(it))
      .map((it) => it.trim())
      .map((p) => p.replace(/[;[\]]/g, '').trim())
      .filter(Boolean);

    if (parts.length === 0) return null;
    return `[${parts.join(';')}]`;
  }

  if (typeof value === 'string') {
    const s = value.trim();
    if (!s) return null;

    // If string already starts and ends with brackets, treat as tags
    if (s.startsWith('[') && s.endsWith(']')) {
      // Try to parse as JSON array first (e.g., ["a","b"])
      if (s.includes(',')) {
        try {
          const parsed = JSON.parse(s);
          if (Array.isArray(parsed)) {
            const parts = parsed
              .map((it) => pickStringish(it))
              .filter((it): it is string => Boolean(it))
              .map((it) => it.trim())
              .map((p) => p.replace(/[;[\]]/g, '').trim())
              .filter(Boolean);

            if (parts.length === 0) return null;
            return `[${parts.join(';')}]`;
          }
        } catch {
          // fall through - not a valid JSON array, treat as single value
        }
      }

      // If not a JSON array or parsing failed, return as-is (e.g., [Knockout])
      return s;
    }
  }

  return null;
};

export const normalizeCode = (value: unknown): string | null => {
  if (value === null || value === undefined) return null;

  // If it's an array, it's not a code
  if (Array.isArray(value)) return null;

  // Extract string value from number or string
  const s = pickStringish(value);
  if (!s) return null;

  // Return the trimmed value
  return s;
};

/**
 * Checks if a value is a bracket-wrapped string (e.g., "[Knockout]", "[test1;test2]")
 * @param value - The value to check
 * @returns true if the value is a string that starts with '[' and ends with ']'
 */
export const isBracketWrappedString = (value: unknown): boolean => {
  if (typeof value !== 'string') return false;

  const trimmed = value.trim();
  return trimmed.startsWith('[') && trimmed.endsWith(']');
};

export const normalizeStakes = (value: unknown): string | null => {
  if (value === null || value === undefined) return null;
  if (Array.isArray(value) && value.length >= 2)
    return `${value[0]}|${value[1]}`;
  const raw = String(value);
  const s = decodeTwice(raw)
    .replace(/%257C/gi, '%7c')
    .replace(/%7C/gi, '|')
    .trim();
  const bracket = /^\[(.*)\]$/.exec(s);
  const inner = bracket ? bracket[1] : s;
  const cleaned = inner.replace(/['"]/g, '');
  const parts = cleaned.split(/[|;,]\s*/).filter(Boolean);
  return parts.length >= 2 ? `${parts[0]}|${parts[1]}` : cleaned;
};

export const NORMALIZERS: Record<string, (v: unknown) => string | null> = {
  stakes: normalizeStakes,
  hostedmode: normalizeHostedmode,
  component: normalizeComponent,
  tags: normalizeTags,
  code: normalizeCode,
};
